import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getChildSessions } from '../../services/api';
import SignOutButton from '../../components/SignOutButton';

// ─── Brand tokens (parent interface) ──────────────────────────────────────────

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  surfaceSubtle: '#F1F5F9',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  accentBorder: '#C7D7FE',
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',
  green: '#10B981',
  greenDark: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
  skeletonBase: '#F1F5F9',
  skeletonHighlight: '#E2E8F0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT = "'DM Sans', sans-serif";

const MODE_LABEL = {
  homework:    'Homework',
  explore:     'Explore',
  revision:    'Revision',
  code:        'Code',
  eleven_plus: '11+ Prep',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

function duration(startedAt, endedAt) {
  if (!endedAt) return 'In progress';
  const mins = Math.round((endedAt - startedAt) / 60000);
  if (mins < 1) return '< 1 min';
  return `${mins} min${mins !== 1 ? 's' : ''}`;
}

// ─── Brand mark (matches Dashboard / SessionDetail / SafeguardingAlerts) ──────

function LanternBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0, 102, 255, 0.3)',
      }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="5" y="3" width="6" height="8" rx="1" stroke="#fff" strokeWidth="1.4"/>
          <path d="M6.5 3V2h3v1M5 11h6M7 13h2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </div>
      <span style={{
        fontFamily: HEADING_FONT, fontSize: 17, fontWeight: 700,
        color: C.text, letterSpacing: '-0.01em',
      }}>Lantern</span>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonBlock({ width, height, style }) {
  return (
    <div aria-hidden="true" style={{
      background: C.skeletonBase,
      backgroundImage: `linear-gradient(90deg, ${C.skeletonBase} 0%, ${C.skeletonHighlight} 50%, ${C.skeletonBase} 100%)`,
      backgroundSize: '200% 100%',
      animation: 'cs-shimmer 1.4s ease-in-out infinite',
      borderRadius: 6, width, height, ...style,
    }}/>
  );
}

function ChildSessionsSkeleton() {
  return (
    <div>
      <SkeletonBlock width={220} height={30} style={{ marginBottom: 24 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}>
            <div style={{ flex: 1 }}>
              <SkeletonBlock width={200} height={16} style={{ marginBottom: 8 }} />
              <SkeletonBlock width={240} height={12} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <SkeletonBlock width={90} height={14} style={{ marginBottom: 6, marginLeft: 'auto' }} />
              <SkeletonBlock width={60} height={20} style={{ marginLeft: 'auto', borderRadius: 999 }} />
            </div>
            <SkeletonBlock width={14} height={14} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ChildSessions() {
  const { childId }             = useParams();
  const [sessions, setSessions] = useState(null);
  const [error, setError]       = useState(null);
  const navigate                = useNavigate();

  useEffect(() => {
    getChildSessions(childId)
      .then(r => setSessions(r.data.sessions))
      .catch((err) => {
        if (err.response?.status === 401) {
          navigate('/parent/login');
          return;
        }
        setError('Could not load sessions.');
      });
  }, [childId, navigate]);

  return (
    <>
      <style>{`
        @keyframes cs-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .cs-back:hover { background: ${C.surfaceMuted}; border-color: ${C.borderStrong}; }
        .cs-row {
          transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease, transform 90ms ease;
        }
        .cs-row:hover {
          border-color: ${C.accentBorder};
          background: ${C.accentSoft};
          box-shadow: 0 2px 6px rgba(0, 102, 255, 0.08);
        }
        .cs-row:focus-visible {
          outline: none;
          border-color: ${C.accent};
          box-shadow: 0 0 0 4px ${C.accent}22;
        }
        .cs-row:hover .cs-chevron { transform: translateX(2px); color: ${C.accent}; }
        .cs-chevron { transition: transform 160ms ease, color 160ms ease; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
      }}>
        {/* Top nav */}
        <div style={{
          borderBottom: `1px solid ${C.border}`,
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 5,
        }}>
          <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <LanternBrand />
            <SignOutButton />
          </div>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px 56px' }}>

          {/* Back button */}
          {!error && (
            <button
              className="cs-back"
              onClick={() => navigate('/parent/dashboard')}
              style={{
                fontFamily: BODY_FONT, fontSize: 13, fontWeight: 600,
                padding: '8px 14px',
                background: '#fff',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 24,
                transition: 'background 160ms ease, border-color 160ms ease',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M11 7H3M7 3l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to dashboard
            </button>
          )}

          {error && (
            <div style={{
              background: C.redSoft,
              border: `1px solid ${C.redBorder}`,
              borderRadius: 10,
              padding: '14px 18px',
              color: C.redText,
              fontSize: 14,
            }}>{error}</div>
          )}

          {!sessions && !error && <ChildSessionsSkeleton />}

          {sessions && (
            <>
              <h1 style={{
                margin: '0 0 24px',
                fontFamily: HEADING_FONT,
                fontSize: 30,
                fontWeight: 700,
                color: C.text,
                letterSpacing: '-0.025em',
                lineHeight: 1.15,
              }}>Session history</h1>

              {sessions.length === 0 ? (
                <div style={{
                  background: C.surfaceMuted,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  padding: '40px 24px',
                  textAlign: 'center',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: '#fff',
                    border: `1px solid ${C.border}`,
                    margin: '0 auto 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.textSubtle,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                      <path d="M4 7c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2h-7l-4 3v-3H6c-1.1 0-2-.9-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 style={{
                    margin: '0 0 6px',
                    fontFamily: HEADING_FONT,
                    fontSize: 18,
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: '-0.015em',
                  }}>No sessions yet</h2>
                  <p style={{
                    margin: 0,
                    fontSize: 14,
                    color: C.textMuted,
                    lineHeight: 1.5,
                    maxWidth: 360,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}>
                    When your child starts using Lantern, their sessions will show up here.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sessions.map(s => {
                    const isActive = !s.sealed;
                    const hasFlags = s.safeguarding_flags > 0;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className="cs-row"
                        onClick={() => navigate(`/parent/session/${s.id}`)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          textAlign: 'left',
                          background: C.surface,
                          border: `1px solid ${C.border}`,
                          borderRadius: 12,
                          padding: '16px 20px',
                          cursor: 'pointer',
                          fontFamily: BODY_FONT,
                          color: C.text,
                          gap: 16,
                          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                        }}
                      >
                        {/* Left: subject + meta */}
                        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            flexWrap: 'wrap',
                            marginBottom: 4,
                          }}>
                            <span style={{
                              fontFamily: HEADING_FONT,
                              fontWeight: 700,
                              fontSize: 15,
                              color: C.text,
                              letterSpacing: '-0.01em',
                            }}>
                              {s.subject} <span style={{ color: C.textSubtle, fontWeight: 600 }}>·</span> {MODE_LABEL[s.mode] || s.mode}
                            </span>
                            {hasFlags && (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.redText,
                                background: C.redSoft,
                                border: `1px solid ${C.redBorder}`,
                                borderRadius: 999,
                                padding: '2px 9px',
                              }}>
                                <span style={{
                                  width: 6, height: 6, borderRadius: '50%',
                                  background: C.red,
                                }}/>
                                {s.safeguarding_flags} flag{s.safeguarding_flags !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: C.textMuted }}>
                            {formatDate(s.started_at)}
                            <span style={{ color: C.textSubtle, margin: '0 8px' }}>·</span>
                            {duration(s.started_at, s.ended_at)}
                          </div>
                        </div>

                        {/* Right: count + status */}
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: 6,
                          flexShrink: 0,
                        }}>
                          <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>
                            {s.message_count} message{s.message_count !== 1 ? 's' : ''}
                          </div>
                          {isActive && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              fontSize: 11,
                              fontWeight: 600,
                              color: C.accentDark,
                              background: C.accentSoft,
                              border: `1px solid ${C.accentBorder}`,
                              borderRadius: 999,
                              padding: '2px 9px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}>
                              <span style={{
                                width: 6, height: 6, borderRadius: '50%',
                                background: C.accent,
                              }}/>
                              Active
                            </span>
                          )}
                        </div>

                        {/* Chevron */}
                        <svg className="cs-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: C.textSubtle, flexShrink: 0 }}>
                          <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
